import { supabase } from "@/lib/supabase";
import { API_ENDPOINTS } from "../constants/api";
import { TOKEN_API } from "../constants/enviroment";

export interface Invitation {
  id: number;
  account_id: string;
  email: string;
  role: string;
  status: string;
  invited_by: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  accepted_at: string | null;
}

const DEFAULT_ERROR = "Failed to send invitation.";

const SESSION_ERROR_MESSAGE = "Session expired or invalid. Please sign in again.";

function parseErrorDetail(body: unknown, status: number): string {
  if (body === null || typeof body !== "object") return DEFAULT_ERROR;
  const data = body as { detail?: unknown };
  const detail = data.detail;
  if (status === 401) return SESSION_ERROR_MESSAGE;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((e: { msg?: string }) => e.msg)
      .filter(Boolean) as string[];
    return messages.length ? messages.join(" ") : DEFAULT_ERROR;
  }
  if (typeof detail === "string") return detail;
  return DEFAULT_ERROR;
}

export type SendInvitationResult =
  | { ok: true }
  | { ok: false; error: string };

export async function sendInvitation(email: string): Promise<SendInvitationResult> {
  const { data } = await supabase.auth.getSession();
  const userId = data.session?.user?.id;
  if (!userId) {
    return { ok: false, error: "Session expired. Please sign in again." };
  }
  if (!TOKEN_API) {
    return { ok: false, error: "API key is not configured (VITE_TOKEN_API)." };
  }

  const url = `${API_ENDPOINTS.createInvitation}?user_id=${encodeURIComponent(userId)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${TOKEN_API}`,
    },
    body: JSON.stringify({
      email: email.trim(),
      role: "member",
      expires_in_days: 7,
    }),
  });

  if (res.ok) return { ok: true };

  const body = await res.json().catch(() => ({}));
  return { ok: false, error: parseErrorDetail(body, res.status) };
}

async function getAuthParams(): Promise<{ userId: string; authHeader: string } | { error: string }> {
  const { data } = await supabase.auth.getSession();
  const userId = data.session?.user?.id;
  if (!userId) return { error: "Session expired. Please sign in again." };
  if (!TOKEN_API) return { error: "API key is not configured (VITE_TOKEN_API)." };
  return { userId, authHeader: `Bearer ${TOKEN_API}` };
}

export async function getInvitations(status?: string): Promise<Invitation[] | { error: string }> {
  const params = await getAuthParams();
  if ("error" in params) return params;
  const { userId, authHeader } = params;
  const query = new URLSearchParams({ user_id: userId });
  if (status) query.set("status", status);
  const url = `${API_ENDPOINTS.createInvitation}?${query.toString()}`;
  const res = await fetch(url, { headers: { Authorization: authHeader } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    return { error: parseErrorDetail(body, res.status) };
  }
  return res.json();
}

export type CancelInvitationResult =
  | { ok: true }
  | { ok: false; error: string };

export async function cancelInvitation(
  invitationId: number
): Promise<CancelInvitationResult> {
  const params = await getAuthParams();
  if ("error" in params) return { ok: false, error: params.error };
  const { userId, authHeader } = params;
  const url = `${API_ENDPOINTS.createInvitation}/${invitationId}/cancel?user_id=${encodeURIComponent(userId)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: authHeader },
  });
  if (res.ok) return { ok: true };
  const body = await res.json().catch(() => ({}));
  return { ok: false, error: parseErrorDetail(body, res.status) };
}

export type ResendInvitationResult =
  | { ok: true }
  | { ok: false; error: string };

export async function resendInvitation(
  invitationId: number
): Promise<ResendInvitationResult> {
  const params = await getAuthParams();
  if ("error" in params) return { ok: false, error: params.error };
  const { userId, authHeader } = params;
  const url = `${API_ENDPOINTS.createInvitation}/${invitationId}/resend?user_id=${encodeURIComponent(userId)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: authHeader },
  });
  if (res.ok) return { ok: true };
  const body = await res.json().catch(() => ({}));
  return { ok: false, error: parseErrorDetail(body, res.status) };
}

